import { useEffect, useState, type ReactNode } from 'react'

import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ApiError } from '@/lib/problem-details'

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  /** Extra detail — the amounts, accounts, and dates the operator is agreeing to. */
  children?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
  /**
   * Requires the operator to type this exact word before the action unlocks. Reserved for the
   * irreversible end of the range — reversals, write-offs, security-deposit offsets — where a
   * misplaced click costs a customer real money.
   */
  confirmationPhrase?: string
  isPending?: boolean
  error?: ApiError | null
  onConfirm: () => void
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  confirmationPhrase,
  isPending = false,
  error = null,
  onConfirm,
}: ConfirmDialogProps) {
  const [typedPhrase, setTypedPhrase] = useState('')

  useEffect(() => {
    if (!open) {
      setTypedPhrase('')
    }
  }, [open])

  const phraseSatisfied = !confirmationPhrase || typedPhrase.trim() === confirmationPhrase

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {children}

          {confirmationPhrase ? (
            <div className="space-y-1.5">
              <Label htmlFor="confirm-phrase">
                Type <span className="font-mono font-semibold">{confirmationPhrase}</span> to continue
              </Label>
              <Input
                id="confirm-phrase"
                value={typedPhrase}
                autoComplete="off"
                onChange={(event) => setTypedPhrase(event.target.value)}
              />
            </div>
          ) : null}

          {error ? <ProblemAlert error={error} /> : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={isPending || !phraseSatisfied}
          >
            {isPending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
